// VerdictCopyService — structured, rotating social-style reactions mapped to the
// existing (deterministic) MOIRÉ LOOK score. Only the presentation copy rotates;
// the scoring engine is untouched. "CLOCK IT." is the reserved top-tier signature.
// Phrases are refreshed here without ever changing score thresholds.

const POOLS = {
  exceptional: [
    "No notes.", "You ate that.", "Ate. No crumbs.", "This fit ate.",
    "You understood the assignment.", "Yeah, this is the one.", "The fit is fitting.",
    "Certified.", "That's hard.", "Main character energy.", "+1000 aura.", "Say less.", "Wear it.",
    "Keep this one.", "Yeah, don't touch it.",
  ],
  strong: [
    "You ate.", "This works.", "The fit is fitting.", "Okay, you snapped.", "This one's clean.",
    "You cooked.", "Strong look.", "Yeah… keep this.", "It's giving.", "That's a look.",
    "This has personality.", "The proportions are doing their job.",
  ],
  good: [
    "Nicely done.", "This is clean.", "I see the vision.", "It's giving.", "Solid fit.",
    "You're onto something.", "This works.", "Low-key hard.",
  ],
  decent: [
    "I see the vision.", "Almost there.", "We're close.", "One tweak away.",
    "The idea is there.", "Let me fix one thing.",
  ],
  improve: [
    "We can do better.", "Let's tighten this up.", "One change would help a lot.",
    "The pieces are competing.", "Keep the idea. Change the execution.",
  ],
};

// Group Check reactions — the crew as a whole, never individuals.
const GROUP_POOLS = {
  exceptional: [
    "The group understood the assignment.", "Everybody showed up.", "No notes.", "The crew ate.",
    "Yeah, this is a moment.", "The fits are fitting.", "Group aura: secured.", "Everybody came correct.",
  ],
  strong: [
    "The crew ate.", "Everybody showed up.", "This group is clean.", "Yeah, y'all got it.",
    "The looks are working.", "Strong lineup.", "You understood the assignment.",
  ],
  good: [
    "This works.", "Strong group look.", "I see the vision.", "The crew is coordinated.",
    "Nice lineup.", "You're onto something.",
  ],
  decent: [
    "Almost there.", "One look is shifting the balance.", "The idea is there.",
    "A small adjustment would pull this together.",
  ],
  improve: [
    "We can tighten this up.", "The group needs one styling adjustment.",
    "The looks are fighting for different occasions.",
  ],
};

// MOIRÉ HOME — a more refined, design-aware voice (occasionally playful).
const HOME_POOLS = {
  exceptional: [
    "This room is sorted.", "Everything belongs.", "The balance is right.", "Now we're talking.",
    "That's the room.", "Warm. Balanced. Done.", "You understood the space.", "The space is giving.",
  ],
  strong: [
    "Beautifully done.", "This works beautifully.", "Really considered.", "This is coming together.",
    "A calm, cohesive space.", "Nearly there — and lovely.",
  ],
  good: [
    "This works.", "Good bones here.", "You're onto something.", "Nicely balanced.", "A composed little space.",
  ],
  decent: [
    "Almost there.", "One change would lift this.", "The idea is right.", "Close — a small tweak.",
  ],
  improve: [
    "Let's tighten this up.", "A couple of changes would help.", "The pieces are competing a little.",
  ],
};
const HOME_RECENT = {};
function rotateHome(band) {
  const pool = HOME_POOLS[band];
  const recent = HOME_RECENT[band] || [];
  const fresh = pool.filter((p) => !recent.includes(p));
  const choices = fresh.length ? fresh : pool;
  const pick = choices[Math.floor(Math.random() * choices.length)];
  HOME_RECENT[band] = [...recent, pick].slice(-Math.max(1, Math.min(pool.length - 1, 5)));
  return pick;
}

// Avoid repeating the same phrase to the same user within a session where practical.
const RECENT = {};

function rotate(band) {
  const pool = POOLS[band];
  const recent = RECENT[band] || [];
  const fresh = pool.filter((p) => !recent.includes(p));
  const choices = fresh.length ? fresh : pool;
  const pick = choices[Math.floor(Math.random() * choices.length)];
  const keep = Math.min(pool.length - 1, 6);
  RECENT[band] = [...recent, pick].slice(-Math.max(1, keep));
  return pick;
}

const GROUP_RECENT = {};
function rotateGroup(band) {
  const pool = GROUP_POOLS[band];
  const recent = GROUP_RECENT[band] || [];
  const fresh = pool.filter((p) => !recent.includes(p));
  const choices = fresh.length ? fresh : pool;
  const pick = choices[Math.floor(Math.random() * choices.length)];
  GROUP_RECENT[band] = [...recent, pick].slice(-Math.max(1, Math.min(pool.length - 1, 5)));
  return pick;
}

export const VerdictCopyService = {
  bandFor(score) {
    const s = Number(score) || 0;
    if (s >= 9.0) return "exceptional";
    if (s >= 8.0) return "strong";
    if (s >= 7.0) return "good";
    if (s >= 6.0) return "decent";
    return "improve";
  },

  // Returns { tier, signature, reaction }. signature is the reserved "CLOCK IT 🤏🏼"
  // headline (top tier only); reaction is the short rotating social line.
  pick(score) {
    const tier = this.bandFor(score);
    return {
      tier,
      signature: tier === "exceptional" ? "CLOCK IT 🤏🏼" : null,
      reaction: rotate(tier),
    };
  },

  reset() { Object.keys(RECENT).forEach((k) => delete RECENT[k]); },

  // Group Check verdict — reserved "CLOCK IT 🤏🏼" top-tier signature, rotating group reaction.
  pickGroup(score) {
    const tier = this.bandFor(score);
    return {
      tier,
      signature: tier === "exceptional" ? "CLOCK IT 🤏🏼" : null,
      reaction: rotateGroup(tier),
    };
  },

  // MOIRÉ HOME verdict — CLOCK IT 🤏🏼 signature, refined interiors reaction pool.
  pickHome(score) {
    const tier = this.bandFor(score);
    return {
      tier,
      signature: tier === "exceptional" ? "CLOCK IT 🤏🏼" : null,
      reaction: rotateHome(tier),
    };
  },
};
